import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BaseStatusDTO,
  GetBaseStatusResponse,
} from "../../models/BaseStatus.model";
import {
  getBaseStatus,
  createBaseStatus,
  deleteBaseStatus,
  updateStatusPositionIdx,
} from "../thunks/baseStatusThunks";
import { RootState } from "../store";

interface BaseStatusState {
  baseStatuses: BaseStatusDTO[];
  loading: boolean;
  operationLoading: boolean;
  error: string | null;
  success: boolean;
  message: string;
  initialized: boolean;
}

const initialState: BaseStatusState = {
  baseStatuses: [],
  loading: false,
  operationLoading: false,
  error: null,
  success: false,
  message: "",
  initialized: false,
};

const baseStatusSlice = createSlice({
  name: "baseStatus",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.success = false;
      state.message = "";
    },
    resetBaseStatusState: (state) => {
      Object.assign(state, initialState);
    },
    resetInitialized: (state) => {
      state.initialized = false;
      state.baseStatuses = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Base Statuses
      .addCase(getBaseStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getBaseStatus.fulfilled,
        (state, action: PayloadAction<GetBaseStatusResponse>) => {
          state.baseStatuses = action.payload.baseStatuses || [];
          state.initialized = true;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(getBaseStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בטעינת הסטטוסים";
        state.initialized = false;
      })

      // Create Base Status
      .addCase(createBaseStatus.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(createBaseStatus.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "הסטטוס נשמר בהצלחה";
        state.error = null;

        const statusId = action.payload.baseStatusID;
        const statusRequest = action.meta.arg;

        if (statusRequest.baseStatusID && statusRequest.baseStatusID > 0) {
          // עדכון סטטוס קיים
          const existingIndex = state.baseStatuses.findIndex(
            (status) => status.baseStatusID === statusRequest.baseStatusID
          );

          if (existingIndex !== -1) {
            state.baseStatuses[existingIndex] = {
              ...state.baseStatuses[existingIndex],
              baseStatusName: statusRequest.baseStatusName,
              baseStatusColor: statusRequest.baseStatusColor,
              baseStatusIsDeal: statusRequest.baseStatusIsDeal,
              baseStatusIsLost: statusRequest.baseStatusIsLost,
            };
          }
        } else {
          // יצירת סטטוס חדש
          const newStatus: BaseStatusDTO = {
            baseStatusID: statusId,
            baseStatusName: statusRequest.baseStatusName,
            baseStatusColor: statusRequest.baseStatusColor,
            baseStatusIsDeal: statusRequest.baseStatusIsDeal,
            baseStatusIsLost: statusRequest.baseStatusIsLost,
            baseStatusPositionIdx: state.baseStatuses.length, // מציבים בסוף
          };

          state.baseStatuses.push(newStatus);
        }
      })
      .addCase(createBaseStatus.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה ביצירת הסטטוס";
        state.success = false;
      })

      // Delete Base Status
      .addCase(deleteBaseStatus.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(deleteBaseStatus.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "הסטטוס נמחק בהצלחה";
        state.error = null;

        // הסר את הסטטוס מהמערך המקומי
        const deletedStatusId = action.meta.arg.baseStatusID;
        state.baseStatuses = state.baseStatuses.filter(
          (status) => status.baseStatusID !== deletedStatusId
        );
      })
      .addCase(deleteBaseStatus.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה במחיקת הסטטוס";
        state.success = false;
      })

      // Update Status Position
      .addCase(updateStatusPositionIdx.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(updateStatusPositionIdx.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.success = true;
        state.message = action.payload.message || "המיקום עודכן בהצלחה";
        state.error = null;

        // איפוס לטעינה מחדש
        state.initialized = false;
      })
      .addCase(updateStatusPositionIdx.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload || "שגיאה בעדכון המיקום";
        state.success = false;
      });
  },
});

// Selectors
export const selectBaseStatuses = (state: RootState) =>
  state.baseStatus.baseStatuses;
export const selectBaseStatusLoading = (state: RootState) =>
  state.baseStatus.loading;
export const selectBaseStatusError = (state: RootState) =>
  state.baseStatus.error;
export const selectBaseStatusInitialized = (state: RootState) =>
  state.baseStatus.initialized;
export const selectBaseStatusOperationLoading = (state: RootState) =>
  state.baseStatus.operationLoading;
export const selectBaseStatusSuccess = (state: RootState) =>
  state.baseStatus.success;
export const selectBaseStatusMessage = (state: RootState) =>
  state.baseStatus.message;

export const { clearMessages, resetBaseStatusState, resetInitialized } =
  baseStatusSlice.actions;

export default baseStatusSlice.reducer;
